import { Injectable, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, BehaviorSubject, of, map, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Activity } from '../models/activity.model';
import { UserService } from './user.service';
import { AuthService } from './auth.service';
import { user } from '../models/user.model';
import { ListSettingsService } from './listsettings.service';

@Injectable({ providedIn: 'root' })
export class ActivityService implements OnInit {
  http = inject(HttpClient);
  userService = inject(UserService);
  authService = inject(AuthService);
  /* Der ListSettingsService liefert die Icon- und Farb-Definitionen aus der Postgres-Datenbank */
  private listService = inject(ListSettingsService);

  users: user[] = [];

  private apiUrl = environment.apiUrl + '/Activity';

  private activitiesCache: Activity[] | null = null;
  /* Das BehaviorSubject informiert alle abonnierten Listen-Komponenten über Datenänderungen */
  private activitiesSubject = new BehaviorSubject<Activity[] | null>(null);

  ngOnInit(): void {
    this.userService.getUsersObservable().subscribe((response) => {
      this.users = response;
    });
  }
  // Dynamischer Aufbau der Dropdown-Optionen für Aktivitätstypen, basierend auf den Datenbankeinträgen
  getDynamicTypeOptions(): Observable<any[]> {
    return this.listService.getListByName('activity_types').pipe(
      map((settings) =>
        settings.map((s) => ({
          label: s.value,
          value: s.value,
          icon: s.icon || 'pi pi-tag',
          color: s.color || '#6B7280',
        })),
      ),
    );
  }

  enrichActivities(activities: Activity[]): Observable<Activity[]> {
    return this.listService.getListByName('activity_types').pipe(
      map((settings) =>
        // Leere Einträge aussortieren: liefert ein Endpunkt nichts zurück, kippte die
        // Zuordnung unten sonst mit einem TypeError und der Cache bliebe unberührt.
        activities
          .filter((activity) => activity?.activityType)
          .map((activity) => {
            const match = settings.find(
              (s) => s.value === activity.activityType.typeName,
            );
            // Neues Objekt per Spread-Operator erstellen!
            return {
              ...activity,
              dbIcon: match?.icon || null,
              dbColor: match?.color || null,
            };
          }),
      ),
    );
  }

  clearActivityCache(): void {
    this.activitiesCache = null;
    /* Setzt den Stream zurück, damit Abonnenten über den leeren Zustand informiert werden */
    this.activitiesSubject.next(null);
    console.log('Clearing activity cache...');
  }

  hasCachedActivities(): boolean {
    return this.activitiesCache !== null;
  }

  getUserActivities(): Observable<Activity[]> {
    if (this.activitiesCache) {
      return of(this.activitiesCache);
    }
    return this.fetchUserActivities();
  }
  fetchUserActivities(): Observable<Activity[]> {
    const roleId = this.authService.getUserRole();
    const userId = this.authService.getCurrentUserId();
    let activityDelegate$: Observable<Activity[]>;

    switch (roleId) {
      case 4:
        activityDelegate$ = this.getAllActivities();
        break;
      case 3:
      case 2:
        activityDelegate$ = this.getDocentActivitiesByUserId(userId);
        break;
      default:
        activityDelegate$ = this.userService
          .getUserDetails(userId)
          .pipe(map((user) => user.activities || []));
        break;
    }

    /* WICHTIG: Erst laden, DANN mappen, DANN in das Subject pushen */
    return activityDelegate$.pipe(
      switchMap((activities) => this.enrichActivities(activities)),
      tap((enriched) => {
        this.activitiesCache = enriched;
        this.activitiesSubject.next(enriched);
      }),
    );
  }

  createActivity(activity: Activity): Observable<Activity> {
    return this.http.post<Activity>(`${this.apiUrl}/CreateActivity`, activity);
  }
  addActivity(activity: Activity): void {
    /*  Nutzerzuordnung im UserService aktualisieren */
    activity.users.forEach((u) =>
      this.userService.addToUserActivities(u.userId, activity),
    );

    /*  Neue Aktivität starten */
    this.enrichActivities([activity]).subscribe((enrichedArray) => {
      /* Da wir nur eine Aktivität geschickt haben, nehmen wir das erste Element */
      const activityWithStyles = enrichedArray[0];

      if (this.activitiesCache) {
        /* Die neue, angereicherte Aktivität zum Cache hinzufügen */
        this.activitiesCache = [...this.activitiesCache, activityWithStyles];
      } else {
        /* Falls der Cache noch nie geladen wurde, initialen Fetch auslösen */
        this.fetchUserActivities().subscribe();
        return;
      }
      /* Das Subject informieren, damit die Liste im UI sofort aktualisiert wird */
      this.activitiesSubject.next(this.activitiesCache);
    });
  }

  //#region API CALLS

  getActivitesObservable(): Observable<Activity[] | null> {
    return this.activitiesSubject.asObservable();
  }

  getActivityById(id: number): Observable<Activity> {
    /* Ruft eine einzelne Aktivität ab und reichert sie mit Icon/Farbe an */
    return this.http
      .get<Activity>(`${this.apiUrl}/GetActivityById/${id}`)
      .pipe(
        switchMap((activity) =>
          this.enrichActivities([activity]).pipe(map((a) => a[0])),
        ),
      );
  }

  getAllActivities(): Observable<Activity[]> {
    return this.http.get<Activity[]>(`${this.apiUrl}/GetAllActivities`);
  }

  updateActivity(activity: Activity): void {
    /* Aktualisiert eine bestehende Aktivität und lädt die Styles erneut */
    this.enrichActivities([activity]).subscribe((enriched) => {
      const updated = enriched[0];
      if (!updated) return;
      if (!this.activitiesCache) {
        this.fetchUserActivities().subscribe({
          next: (activities) =>
            this.updateActivityInternal(updated, activities),
        });
      } else {
        this.updateActivityInternal(updated, this.activitiesCache);
      }
    });
  }

  private updateActivityInternal(activity: Activity, cache: Activity[]): void {
    const index = cache.findIndex((a) => a.activityId === activity.activityId);

    if (index !== -1) {
      const oldUserIds = cache[index].users?.map((u) => u.userId);
      this.userService.updateUserActivities(oldUserIds, activity);

      cache[index] = activity;
      this.activitiesCache = [...cache];
      this.activitiesSubject.next(this.activitiesCache);
    } else {
      this.activitiesCache = [...cache, activity];
      this.activitiesSubject.next(this.activitiesCache);
    }
  }

  removeActivityFromCache(activityId: number): void {
    if (this.activitiesCache) {
      this.activitiesCache = this.activitiesCache.filter(
        (a) => a.activityId !== activityId,
      );
      this.activitiesSubject.next(this.activitiesCache);
    }
  }

  deleteActivity(activityId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/DeleteActivity/${activityId}`);
  }

  getDocentActivitiesByUserId(userId: number): Observable<Activity[]> {
    return this.http.get<Activity[]>(
      `${this.apiUrl}/GetDocentActivitiesByUserId/${userId}`,
    );
  }

  //#endregion
}
